import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/seo/SEOHead";
import { OrganizationSchema } from "@/components/seo/OrganizationSchema";
import { FAQSchema } from "@/components/seo/SchemaMarkup";
import { scrollToSection } from "@/lib/smoothScroll";
import { MarketingThemeProvider } from "@/components/marketing/v2/MarketingThemeProvider";
import { MarketingNav } from "@/components/marketing/v2/MarketingNav";
import { MarketingFooter } from "@/components/marketing/v2/MarketingFooter";
import { HeroChat } from "@/components/marketing/v2/HeroChat";
import { LogoStrip } from "@/components/marketing/v2/LogoStrip";
import { StatsStrip } from "@/components/marketing/v2/StatsStrip";
import { HowItWorks } from "@/components/marketing/v2/HowItWorks";
import { ComparisonTable } from "@/components/marketing/v2/ComparisonTable";
import { SecurityBand } from "@/components/marketing/v2/SecurityBand";
import { FeatureSection } from "@/components/marketing/v2/FeatureSection";
import { TestimonialCarousel } from "@/components/marketing/v2/TestimonialCarousel";
import { PricingTableV2 } from "@/components/marketing/v2/PricingTableV2";
import { FAQAccordion, homepageFaqs } from "@/components/marketing/v2/FAQAccordion";
import { SupportCards } from "@/components/marketing/v2/SupportCards";
import {
  FeedbackInboxMock,
  AIInsightsMock,
  ChannelsMock,
  AnalyticsMock,
} from "@/components/marketing/v2/Mockups";
import { PillButton } from "@/components/marketing/v2/PillButton";

const Index = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.hash) {
      scrollToSection(window.location.hash.substring(1));
    }
  }, []);

  return (
    <MarketingThemeProvider>
      <SEOHead
        title="Anonymous Employee Feedback for HR Teams | SendFeedback.ai"
        description="Anonymous employee feedback that HR actually trusts. AI-powered themes, sentiment, and retention risk in 5 minutes. GDPR compliant. 15-day free trial."
        canonicalUrl="https://sendfeedback.ai"
        ogImage="/og-images/og-home.jpg"
        keywords="anonymous employee feedback, employee engagement, AI HR insights, retention risk, pulse surveys"
      />
      <OrganizationSchema />
      <FAQSchema faqs={homepageFaqs.map((f) => ({ question: f.q, answer: f.a }))} />

      <MarketingNav />

      <main id="main-content">
        <HeroChat />
        <LogoStrip />
        <StatsStrip />
        <HowItWorks />

        <section id="features">
          <FeatureSection
            eyebrow="AI Insights"
            title="See What's Driving Culture & Stop What Isn't"
            description="Theme clustering, sentiment shifts, and retention risk scoring — automatically. No manual tagging, no spreadsheets."
            variant="purple"
            mockup={<AIInsightsMock />}
            proofChip="Surfaces attrition risks an average of 6 weeks earlier"
          />

          <FeatureSection
            eyebrow="Anonymous Feedback Inbox"
            title="Hear What Your Team Won't Say Out Loud"
            description="Always-on anonymous channels for every department — with PII stripped at the database level, before any human sees it."
            variant="hero"
            mockup={<FeedbackInboxMock />}
            proofChip="5× more responses than traditional pulse surveys"
          />

          <FeatureSection
            eyebrow="Smart Channels"
            title="Slice Feedback By Department, Tenure, Or Risk"
            description="Ask the AI in plain English. Filter by team, location, or sentiment. Get the answer in seconds — not next quarter."
            variant="teal"
            mockup={<ChannelsMock />}
            proofChip="Answers in seconds, not next quarter"
          />

          <FeatureSection
            eyebrow="Executive Analytics"
            title="Board-Ready Dashboards In One Click"
            description="Auto-generated executive summaries, attrition risk indices, and engagement health — exportable as PDF or live link."
            variant="sunset"
            mockup={<AnalyticsMock />}
            proofChip="Replaces weeks of manual board prep"
          />
        </section>

        <ComparisonTable />

        <TestimonialCarousel />

        <SecurityBand />

        <SupportCards />

        <PricingTableV2 />

        <FAQAccordion />

        {/* Final CTA */}
        <section className="container mx-auto px-3 lg:px-8 pb-24">
          <div className="relative mkt-gradient-hero mkt-grain rounded-[2rem] overflow-hidden px-6 sm:px-12 py-20 text-center text-white">
            <div className="absolute -right-20 -top-20 w-[400px] h-[400px] rounded-full bg-white/10 blur-3xl" />
            <h2 className="relative text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              Ready For Honest Feedback?
            </h2>
            <p className="relative mt-4 text-lg text-white/85 max-w-xl mx-auto">
              Launch your first anonymous channel in under 5 minutes. No card required.
            </p>
            <div className="relative mt-8 flex flex-wrap justify-center gap-3">
              <PillButton variant="white" size="lg" onClick={() => navigate("/auth?mode=signup")}>
                Start Free Trial
              </PillButton>
              <PillButton variant="outline" size="lg" className="!border-white/30 !text-white hover:!bg-white/10" onClick={() => navigate("/contact")}>
                Book A 15-min Demo
              </PillButton>
            </div>
            <p className="relative mt-5 text-xs text-white/70">
              Join 200+ HR teams · Cancel anytime · GDPR compliant
            </p>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </MarketingThemeProvider>
  );
};

export default Index;
